import { ADDON_MONTHLY, planOf, type PlanId, type PlanPeriod } from '../plans'
import type { Features, FeatureKey } from '../types'

/**
 * Abonelik hesapları (1 Kas 2026): liste fiyatı, ödeme durumu, dönem
 * tarihleri. Tarihler yerel gün, "2026-11-01" biçiminde.
 */

export type PriceMode = 'list' | 'custom' | 'free'

/** "2026-01-31" + 1 ay = "2026-02-28": ay sonu taşmıyor. */
export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const first = new Date(y, m - 1 + months, 1)
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
  const out = new Date(first.getFullYear(), first.getMonth(), Math.min(d, last))
  return dayKey(out)
}

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** İki gün arasındaki fark, gün (b - a). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000)
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number)
  return dayKey(new Date(y, m - 1, d + n))
}

/** Ayın ilk günü: "2026-11-17" → "2026-11-01". */
export const monthStart = (day: string) => `${day.slice(0, 7)}-01`

/**
 * Ödeme günü (2 Kas 2026, kullanıcı: "her ayın ilk iş günü"): dönem
 * bitişi hafta sonuna denk gelirse Pazartesiye kayar. Resmi tatiller
 * sayılmıyor (kullanıcı: yalnız hafta sonu). Sunucudaki
 * public.first_business_day ile aynı kural.
 */
export function businessDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  const wd = new Date(y, m - 1, d).getDay()
  return wd === 6 ? addDays(day, 2) : wd === 0 ? addDays(day, 1) : day
}

/**
 * Ödeme gününden bu kadar gün sonra hala ödeme yoksa işletme salt okunur
 * (kullanıcı, 2 Kas 2026: "ödenmezse beş gün sonra işleme kapat"). Son
 * tarih 2 Kasım ise 7 Kasım'da kapanır. Sunucuda public.tenant_locked_since.
 */
export const GRACE_DAYS = 5

/** Dönem başına ay: yıllık 12, aylık (ya da bilinmeyen) 1. */
export const periodMonths = (period: PlanPeriod | null | undefined) => (period === 'yillik' ? 12 : 1)

/**
 * Liste fiyatı, kuruş, kurumun dönemi başına: paket + açık ek modüller.
 * Yıllıkta paketin yıllık fiyatı (iki ay bedava), ek modüller de 10 ay.
 * Paketsiz kurumda yalnız ek modüller.
 */
export function listPriceKurus(plan: PlanId | null, period: PlanPeriod | null, features: Features): number {
  const yearly = period === 'yillik'
  const p = planOf(plan)
  const base = p ? (yearly ? p.yearly : p.monthly) : 0
  const addons = (Object.keys(ADDON_MONTHLY) as FeatureKey[])
    .filter((k) => features[k])
    .reduce((sum, k) => sum + (ADDON_MONTHLY[k] ?? 0), 0)
  return (base + addons * (yearly ? 10 : 1)) * 100
}

export type PayState =
  | { kind: 'free' }
  | { kind: 'paid'; until: string; days: number }
  | { kind: 'soon'; until: string; days: number }
  /** closed: ödeme gününden GRACE_DAYS geçti, işletme salt okunur (closesOn'dan beri). */
  | { kind: 'late'; since: string; days: number; closed: boolean; closesOn: string | null }
  | { kind: 'trial'; until: string; days: number }
  | { kind: 'none' }

/** Son ödeme gününe bu kadar gün kala "Yaklaşıyor". */
export const SOON_DAYS = 7

/**
 * Kurumun ödeme durumu.
 *
 * paidUntil: son ödemenin kapsadığı dönemin bitişi (o gün dahil değil).
 * Ödeme günü onun ilk iş günü (businessDay). Ödeme yoksa deneme bitişine bakılıyor: deneme
 * sürüyorsa "Deneme", bittiyse o günden beri gecikme. İkisi de yoksa
 * (denemesiz açılmış eski kurum) "Kayıt yok".
 */
export function payState(
  mode: PriceMode,
  paidUntil: string | null,
  trialEnd: string | null,
  today: string,
): PayState {
  if (mode === 'free') return { kind: 'free' }
  if (paidUntil) {
    const due = businessDay(paidUntil)
    const left = daysBetween(today, due)
    if (left < 0) {
      const closesOn = addDays(due, GRACE_DAYS)
      return { kind: 'late', since: due, days: -left, closed: today >= closesOn, closesOn }
    }
    if (left <= SOON_DAYS) return { kind: 'soon', until: due, days: left }
    return { kind: 'paid', until: due, days: left }
  }
  // Deneme bitince satış zaten duruyor (deneme-suresi.sql), ayrıca kapanmıyor.
  if (trialEnd) {
    const left = daysBetween(today, trialEnd)
    if (left >= 0) return { kind: 'trial', until: trialEnd, days: left }
    return { kind: 'late', since: trialEnd, days: -left, closed: false, closesOn: null }
  }
  return { kind: 'none' }
}

/**
 * Bir günde geçerli tutar: özel fiyat bitiş gününden önce özel, o günden
 * sonra liste fiyatı. custom: o gün özel fiyat mı geçerli.
 */
export function priceOn(
  mode: PriceMode,
  customKurus: number | null,
  customUntil: string | null,
  listKurus: number,
  day: string,
): { kurus: number; custom: boolean } {
  if (mode === 'free') return { kurus: 0, custom: false }
  if (mode === 'custom' && customKurus != null && (!customUntil || day < customUntil)) {
    return { kurus: customKurus, custom: true }
  }
  return { kurus: listKurus, custom: false }
}
